import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react';
import { ScrollArea as BaseScrollArea } from '@base-ui/react/scroll-area';
import styles from './ScrollArea.module.css';

type ScrollOrientation = 'vertical' | 'horizontal' | 'both';

export interface IScrollAreaProps {
  /** Which way the content scrolls — `both` for a pan surface (a zoomed map). */
  orientation?: ScrollOrientation;
  /** The root — the box that takes the place in the layout (flex, height cap). */
  className?: string;
  /** The viewport — the element that actually scrolls. A caller that scrolls
   *  it by hand (a jump-to-card) targets this class or `viewportRef`. */
  viewportClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
  /** Anything else the scrolling element carries — `onScroll`, `tabIndex`,
   *  `aria-*`, `role`, `data-*` markers (the app shell's content-pane
   *  attribute, a test id). A `style` here merges over the axis lock; the
   *  class is `viewportClassName`'s job. */
  viewportProps?: Omit<HTMLAttributes<HTMLDivElement>, 'className'> & Record<`data-${string}`, string | undefined>;
  children: ReactNode;
}

/** Base UI's viewport is `overflow: scroll` on both axes; a list scrolls one
 *  way only, so the other is locked rather than left to drift sideways. */
const LOCKED_AXIS: Record<ScrollOrientation, CSSProperties | undefined> = {
  vertical: { overflowX: 'hidden' },
  horizontal: { overflowY: 'hidden' },
  both: undefined,
};

const BARS: Record<ScrollOrientation, Array<'vertical' | 'horizontal'>> = {
  vertical: ['vertical'],
  horizontal: ['horizontal'],
  both: ['vertical', 'horizontal'],
};

/**
 * A scroll container with an overlay scrollbar: no native bar and no gutter;
 * a thin thumb drawn over the edge appears while the content scrolls and fades
 * out shortly after it stops (macOS-style). The thumb can still be dragged.
 * Keyboard, wheel and screen-reader scrolling are the viewport's own — it is a
 * normal scroll container underneath.
 */
export function ScrollArea({
  orientation = 'vertical',
  className,
  viewportClassName,
  viewportRef,
  viewportProps,
  children,
}: IScrollAreaProps) {
  return (
    <BaseScrollArea.Root className={className ? `${styles.root} ${className}` : styles.root}>
      <BaseScrollArea.Viewport
        ref={viewportRef}
        className={viewportClassName ? `${styles.viewport} ${viewportClassName}` : styles.viewport}
        {...viewportProps}
        style={{ ...LOCKED_AXIS[orientation], ...viewportProps?.style }}
      >
        {children}
      </BaseScrollArea.Viewport>
      {BARS[orientation].map((bar) => (
        <BaseScrollArea.Scrollbar key={bar} orientation={bar} className={styles.scrollbar}>
          <BaseScrollArea.Thumb className={styles.thumb} />
        </BaseScrollArea.Scrollbar>
      ))}
    </BaseScrollArea.Root>
  );
}
